import {
  call,
  put, takeEvery,
} from 'redux-saga/effects';
import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import { select } from '~/_shared/utils/saga/effects';
import { type PickAction } from '~/_shared/utils/types/action.type';
import type { BoundaryTerritoryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { boundaryTerritoryGroupsBulkSaveRequest } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED, BOUNDARY_TERRITORY_GROUP_UPDATE_REQUEST,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionTypes';
import {
  activeBoundaryToolBTGIdPerBoundaryGroupToolsStateSelector, boundaryFiltersStateSelector,
} from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.stateSelectors';
import { type BoundaryTerritoryGroupsAction } from '../../../boundaryTerritoryGroups/boundaryTerritoryGroups.action';
import {
  type BoundaryGroupInfoWithBTGs, BTGsGroupedByBoundaryGroupSelector,
  findBoundaryTerritoryGroupSelector, type GetBTGById,
} from '../../../boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  activeBoundaryToolBTGIdPerBoundaryGroupChanged, setBoundaryFilter,
} from './mapSettingsToolsStateBoundary.actionCreators';
import type { BoundaryFiltersState } from './mapSettingsToolsStateBoundary.state';

export function* mapSettingsToolsStateBoundarySagas() {
  yield takeEvery(boundaryTerritoryGroupsBulkSaveRequest, boundaryTerritoryGroupsBulkSaveRequestSaga);
  yield takeEvery(BOUNDARY_TERRITORY_GROUP_UPDATE_REQUEST, resetBoundaryFiltersOnRangesCountChangeSaga);
  yield takeEvery(BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED, updateActiveBoundaryTerritoryGroupsAfterRemoval);
}

function* updateActiveBoundaryTerritoryGroupsAfterRemoval(action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED>) {
  const { boundaryTerritoryGroups: removedBTGs } = action.payload;

  const activeBTGIdPerBoundaryGroup: ReturnType<typeof activeBoundaryToolBTGIdPerBoundaryGroupToolsStateSelector> =
    yield select(activeBoundaryToolBTGIdPerBoundaryGroupToolsStateSelector);
  const boundaryToolTerritoryGroupsByBoundaryGroup: ReadonlyArray<BoundaryGroupInfoWithBTGs> =
    yield select((state) => BTGsGroupedByBoundaryGroupSelector(state).boundaryTool);

  for (const { boundaryGroupId, boundaryTerritoryGroupId } of removedBTGs) {
    if (activeBTGIdPerBoundaryGroup[boundaryGroupId] === boundaryTerritoryGroupId) {
      const boundaryTerritoryGroupsInTheBoundaryGroup = boundaryToolTerritoryGroupsByBoundaryGroup
        .find(item => item.boundaryGroupId === boundaryGroupId);
      const firstBTGInBG = boundaryTerritoryGroupsInTheBoundaryGroup?.boundaryTerritoryGroups?.[0];
      if (firstBTGInBG) {
        yield put(activeBoundaryToolBTGIdPerBoundaryGroupChanged(boundaryGroupId, firstBTGInBG.boundaryTerritoryGroupId));
      }
    }
  }
}

function* boundaryTerritoryGroupsBulkSaveRequestSaga(action: PickAction<BoundaryTerritoryGroupsAction, typeof boundaryTerritoryGroupsBulkSaveRequest.type>) {
  for (const updatedBTG of action.payload.boundaryTerritoryGroups) {
    const { boundaryTerritoryGroupId } = updatedBTG;
    const { rangesCount } = updatedBTG.settings;
    yield call(resetBoundaryFiltersOnRangesCountChange, {
      boundaryTerritoryGroupId,
      newRangesCount: rangesCount,
    });
  }
}

function* resetBoundaryFiltersOnRangesCountChangeSaga(action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_UPDATE_REQUEST>) {
  yield call(resetBoundaryFiltersOnRangesCountChange, {
    boundaryTerritoryGroupId: action.payload.updatedBoundaryTerritoryGroup.boundaryTerritoryGroupId,
    newRangesCount: action.payload.updatedBoundaryTerritoryGroup.settings.rangesCount,
  });
}

function* resetBoundaryFiltersOnRangesCountChange({
  boundaryTerritoryGroupId,
  newRangesCount,
}: {
  boundaryTerritoryGroupId: BoundaryTerritoryGroupId;
  newRangesCount: number | null;
}) {
  const getBoundaryTerritoryGroupById: GetBTGById = yield select<GetBTGById>(findBoundaryTerritoryGroupSelector);
  const boundaryFiltersState: BoundaryFiltersState = yield select<BoundaryFiltersState>(boundaryFiltersStateSelector);

  const currentBoundaryTerritoryGroup = getBoundaryTerritoryGroupById(boundaryTerritoryGroupId);

  const boundaryFilters = currentBoundaryTerritoryGroup
    ? boundaryFiltersState[currentBoundaryTerritoryGroup.boundaryGroupId]
    : null;

  if (!currentBoundaryTerritoryGroup ||
    currentBoundaryTerritoryGroup.settings.rangesCount === newRangesCount ||
    !boundaryFilters
  ) {
    return;
  }

  yield put(setBoundaryFilter(currentBoundaryTerritoryGroup.boundaryGroupId, {
    filteredBoundaries: [],
    filteredBoundaryTerritories: [],
    showAll: boundaryFilters.showAll === TriStateRange.Partial
      ? TriStateRange.Full
      : boundaryFilters.showAll,
  }));
}
